import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import type {
  ChangedFieldItemToken,
  ChangedFieldPath,
  ChangedFieldPathToken,
  ChangeTrackingEntityIdentity,
  DiffContext,
  FindChangedFieldsOptions,
  UnknownRecord,
} from '@typeDefinitions/changeTrackingTypes';

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  !(value instanceof Date);

const getDefaultIdentity = (
  value: UnknownRecord,
): ChangeTrackingEntityIdentity | undefined => {
  const id = value.id ?? value.Id;
  if (typeof id === 'number' && id !== 0 && Number.isFinite(id)) return id;
  if (typeof id === 'string' && id.trim() !== '' && id !== '0') return id;
  return undefined;
};

const defaultScalarEquality = (before: unknown, after: unknown): boolean => {
  if (Object.is(before, after)) return true;
  if (before instanceof Date && after instanceof Date) {
    return before.getTime() === after.getTime();
  }
  if (typeof before === 'number' && typeof after === 'string') {
    return after.trim() !== '' && Number(after) === before;
  }
  if (typeof before === 'string' && typeof after === 'number') {
    return before.trim() !== '' && Number(before) === after;
  }
  return false;
};

const propertyToken = (key: string): ChangedFieldPathToken => ({
  type: 'property',
  key,
});

const itemToken = (
  index: number,
  item: unknown,
  getIdentity: DiffContext['getIdentity'],
): ChangedFieldItemToken => {
  const id = isRecord(item) ? getIdentity(item) : undefined;
  return { type: 'item', index, ...(id === undefined ? {} : { id }) };
};

const addChange = (context: DiffContext, path: ChangedFieldPathToken[]) => {
  if (path.length > 0) context.changes.push({ path });
};

const collectAddedOrRemoved = (
  value: unknown,
  path: ChangedFieldPathToken[],
  changeType: 'added' | 'removed',
  context: DiffContext,
) => {
  const missing = undefined;
  if (
    changeType === 'added'
      ? context.areScalarValuesEqual(missing, value, path)
      : context.areScalarValuesEqual(value, missing, path)
  ) {
    return;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) addChange(context, path);
    value.forEach((item, index) => {
      const itemPath = [...path, itemToken(index, item, context.getIdentity)];
      if (isRecord(item)) addChange(context, itemPath);
      else collectAddedOrRemoved(item, itemPath, changeType, context);
    });
    return;
  }

  if (isRecord(value)) {
    const keys = Object.keys(value).filter((key) => {
      const nextPath = [...path, propertyToken(key)];
      return (
        !context.ignoredFields.has(key) && !context.shouldIgnorePath(nextPath)
      );
    });
    if (keys.length === 0) addChange(context, path);
    keys.forEach((key) =>
      collectAddedOrRemoved(
        value[key],
        [...path, propertyToken(key)],
        changeType,
        context,
      ),
    );
    return;
  }

  addChange(context, path);
};

const findMatchingRecord = (
  records: UnknownRecord[],
  target: UnknownRecord,
  usedIndexes: ReadonlySet<number>,
  getIdentity: DiffContext['getIdentity'],
): number => {
  const targetId = getIdentity(target);
  return records.findIndex((record, index) => {
    if (usedIndexes.has(index)) return false;
    const recordId = getIdentity(record);
    return targetId === undefined
      ? recordId === undefined
      : String(recordId) === String(targetId);
  });
};

function diffValues(
  before: unknown,
  after: unknown,
  path: ChangedFieldPathToken[],
  context: DiffContext,
) {
  if (context.areScalarValuesEqual(before, after, path)) return;

  if (Array.isArray(before) && Array.isArray(after)) {
    diffArrays(before, after, path, context);
    return;
  }

  if (isRecord(before) && isRecord(after)) {
    const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
    keys.forEach((key) => {
      const nextPath = [...path, propertyToken(key)];
      if (
        context.ignoredFields.has(key) ||
        context.shouldIgnorePath(nextPath)
      ) {
        return;
      }

      const beforeHasKey = Object.prototype.hasOwnProperty.call(before, key);
      const afterHasKey = Object.prototype.hasOwnProperty.call(after, key);
      if (!beforeHasKey) {
        collectAddedOrRemoved(after[key], nextPath, 'added', context);
      } else if (!afterHasKey) {
        collectAddedOrRemoved(before[key], nextPath, 'removed', context);
      } else {
        diffValues(before[key], after[key], nextPath, context);
      }
    });
    return;
  }

  if ((before === null || before === undefined) && isRecord(after)) {
    collectAddedOrRemoved(after, path, 'added', context);
  } else if ((after === null || after === undefined) && isRecord(before)) {
    collectAddedOrRemoved(before, path, 'removed', context);
  } else {
    addChange(context, path);
  }
}

const diffArrays = (
  before: unknown[],
  after: unknown[],
  path: ChangedFieldPathToken[],
  context: DiffContext,
) => {
  if (!before.every(isRecord) || !after.every(isRecord)) {
    const equal =
      before.length === after.length &&
      before.every((value, index) =>
        context.areScalarValuesEqual(value, after[index], [
          ...path,
          { type: 'item', index },
        ]),
      );
    if (!equal) addChange(context, path);
    return;
  }

  const usedBeforeIndexes = new Set<number>();
  after.forEach((afterItem, afterIndex) => {
    const itemPath = [
      ...path,
      itemToken(afterIndex, afterItem, context.getIdentity),
    ];
    const beforeIndex = findMatchingRecord(
      before,
      afterItem,
      usedBeforeIndexes,
      context.getIdentity,
    );

    if (beforeIndex < 0) {
      if (!context.isDeletedRecord(afterItem)) {
        addChange(context, itemPath);
      }
      return;
    }

    usedBeforeIndexes.add(beforeIndex);
    if (context.isDeletedRecord(afterItem)) {
      if (!context.isDeletedRecord(before[beforeIndex])) {
        addChange(context, itemPath);
      }
    } else {
      diffValues(before[beforeIndex], afterItem, itemPath, context);
    }
  });

  before.forEach((beforeItem, beforeIndex) => {
    if (
      !usedBeforeIndexes.has(beforeIndex) &&
      !context.isDeletedRecord(beforeItem)
    ) {
      addChange(context, [
        ...path,
        itemToken(beforeIndex, beforeItem, context.getIdentity),
      ]);
    }
  });
};

export const findChangedFieldPaths = (
  before: unknown,
  after: unknown,
  options: FindChangedFieldsOptions = {},
): ChangedFieldPath[] => {
  const context: DiffContext = {
    changes: [],
    ignoredFields: options.ignoredFields ?? new Set(),
    getIdentity: options.getIdentity ?? getDefaultIdentity,
    isDeletedRecord:
      options.isDeletedRecord ?? ((value) => value.operationType === 'Delete'),
    shouldIgnorePath: options.shouldIgnorePath ?? (() => false),
    areScalarValuesEqual: options.areScalarValuesEqual ?? defaultScalarEquality,
  };
  diffValues(before, after, [], context);
  return context.changes;
};

const findHydratedRecordIndex = (
  records: UnknownRecord[],
  target: UnknownRecord,
  fallbackIndex: number,
  getIdentity: NonNullable<FindChangedFieldsOptions['getIdentity']>,
): number => {
  const id = getIdentity(target);
  if (id === undefined)
    return fallbackIndex < records.length ? fallbackIndex : -1;
  return records.findIndex(
    (record) => String(getIdentity(record)) === String(id),
  );
};

const rebaseArray = (
  baseline: unknown,
  before: unknown[],
  after: unknown[],
  getIdentity: NonNullable<FindChangedFieldsOptions['getIdentity']>,
): unknown[] => {
  if (!before.every(isRecord) || !after.every(isRecord))
    return cloneDeep(after);

  const baselineRecords = Array.isArray(baseline)
    ? baseline.filter(isRecord)
    : [];
  const result = cloneDeep(baselineRecords).filter((record, index) => {
    const beforeIndex = findHydratedRecordIndex(
      before,
      record,
      index,
      getIdentity,
    );
    const afterIndex = findHydratedRecordIndex(
      after,
      record,
      index,
      getIdentity,
    );
    return beforeIndex < 0 || afterIndex >= 0;
  });

  after.forEach((afterRecord, index) => {
    const beforeIndex = findHydratedRecordIndex(
      before,
      afterRecord,
      index,
      getIdentity,
    );
    const baselineIndex = findHydratedRecordIndex(
      result,
      afterRecord,
      index,
      getIdentity,
    );
    if (beforeIndex < 0) {
      if (baselineIndex < 0) result.push(cloneDeep(afterRecord));
    } else if (baselineIndex >= 0) {
      result[baselineIndex] = rebaseValue(
        result[baselineIndex],
        before[beforeIndex],
        afterRecord,
        getIdentity,
      ) as UnknownRecord;
    }
  });
  return result;
};

const rebaseRecord = (
  baseline: unknown,
  before: UnknownRecord,
  after: UnknownRecord,
  getIdentity: NonNullable<FindChangedFieldsOptions['getIdentity']>,
): UnknownRecord => {
  const result: UnknownRecord = isRecord(baseline) ? cloneDeep(baseline) : {};
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  keys.forEach((key) => {
    const beforeHasKey = Object.prototype.hasOwnProperty.call(before, key);
    const afterHasKey = Object.prototype.hasOwnProperty.call(after, key);
    if (beforeHasKey === afterHasKey && isEqual(before[key], after[key]))
      return;
    if (!afterHasKey) delete result[key];
    else {
      result[key] = beforeHasKey
        ? rebaseValue(result[key], before[key], after[key], getIdentity)
        : cloneDeep(after[key]);
    }
  });
  return result;
};

function rebaseValue(
  baseline: unknown,
  before: unknown,
  after: unknown,
  getIdentity: NonNullable<FindChangedFieldsOptions['getIdentity']>,
): unknown {
  if (isEqual(before, after)) return baseline;
  if (Array.isArray(before) && Array.isArray(after)) {
    return rebaseArray(baseline, before, after, getIdentity);
  }
  if (isRecord(before) && isRecord(after)) {
    return rebaseRecord(baseline, before, after, getIdentity);
  }
  return cloneDeep(after);
}

export const rebaseHydratedFields = <T>(
  baseline: T,
  before: T,
  after: T,
  getIdentity: FindChangedFieldsOptions['getIdentity'] = getDefaultIdentity,
): T => rebaseValue(baseline, before, after, getIdentity) as T;
